<template>
  <section class="leaderboard" :aria-label="c.title" :aria-busy="busy">
    <header class="board-head">
      <h2>{{ c.title }}</h2>
      <button class="text-button" :disabled="busy" @click="load">{{ c.refresh }}</button>
    </header>

    <p v-if="error" class="board-state" role="alert">{{ c.error }}</p>
    <p v-else-if="!board && busy" class="board-state" role="status">{{ c.loading }}</p>

    <template v-if="board">
      <div class="standing">
        <strong>{{ board.me?.energy ?? 0 }}<small>{{ c.energy }}</small></strong>
        <p>{{ board.toNextRank ? c.gap(board.toNextRank) : c.encourage }}</p>
        <span class="lifetime">{{ c.lifetime }} <strong>{{ board.totalEnergy }}</strong></span>
      </div>

      <ol v-if="board.board.length" class="standings">
        <li v-for="row in board.board" :key="`${row.rank}-${row.alias}-${row.isMe}`" :class="{ mine: row.isMe }">
          <span class="rank">{{ row.rank }}</span>
          <span class="alias">{{ aliasName(row.alias) }} <small v-if="row.isMe">{{ c.you }}</small></span>
          <strong>{{ row.energy }}</strong>
        </li>
      </ol>
      <p v-else class="board-state">{{ board.grouped ? c.emptyGroup : c.empty }}</p>

      <details class="board-rules">
        <summary>{{ c.rules }}</summary>
        <p>{{ c.ruleText }}</p>
        <p>{{ c.reset }} {{ resetLabel }} · UTC</p>
        <p>{{ c.publicNote }}</p>
      </details>
    </template>
  </section>
</template>

<script setup>
import { computed, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@/composables/auth'
import { apiPost } from '@/composables/useApi'
const { locale } = useI18n()
const { user } = useAuth()
const board = ref(null)
const busy = ref(false)
const error = ref(false)
let generation = 0
let resetTimer
// 后端给的昵称是"形容词-动物"的键（如 calm-otter），这里按界面语言显示
const ALIAS_WORDS = {
  calm: '沉着', brave: '勇敢', gentle: '温柔', bright: '明亮', swift: '敏捷', quiet: '安静', happy: '快乐', clever: '聪明', steady: '稳重', sunny: '阳光',
  curious: '好奇', patient: '耐心', lively: '活泼', kind: '友善', bold: '大胆', cozy: '自在', eager: '热情', humble: '谦虚', merry: '开朗', nimble: '灵巧',
  otter: '水獭', fox: '狐狸', panda: '熊猫', owl: '猫头鹰', dolphin: '海豚', koala: '考拉', deer: '小鹿', rabbit: '兔子', penguin: '企鹅', squirrel: '松鼠',
  whale: '鲸鱼', seal: '海豹', sparrow: '麻雀', hedgehog: '刺猬', tiger: '老虎', crane: '仙鹤', turtle: '海龟', lynx: '山猫', swan: '天鹅', bee: '蜜蜂'
}
function aliasName(alias) {
  const [adjective, animal] = alias.split('-')
  // 旧版生成的 Learner-XXXX 昵称在本人下次学习时由后端换成新昵称，这之前原样显示
  if (!ALIAS_WORDS[adjective]) return alias
  if (locale.value === 'zh') return `${ALIAS_WORDS[adjective]}的${ALIAS_WORDS[animal]}`
  return [adjective, animal].map(word => word[0].toUpperCase() + word.slice(1)).join(' ')
}
const c = computed(() => locale.value === 'zh' ? {
  title: '本周周榜', refresh: '刷新', loading: '正在加载周榜…', error: '周榜暂时无法加载，请刷新重试。', energy: '能量',
  gap: n => `再积累 ${n} 能量，就能超过前一名。`, encourage: '按自己的节奏练习，每一点积累都算数。', you: '你', lifetime: '累计能量',
  empty: '本周开始练习后，就会分进一个学习小组，和组里的人比能量。', emptyGroup: '你已进入本周的小组，再练 1 分钟就能上榜。',
  rules: '计分规则', ruleText: '听音每满 1 分钟加 1 能量，每天最多 120；每次完成跟读评分加 1，每天最多 120 次。每周一重新分组，每组最多 15 人，你只和同组的学习者排名，榜单显示组内排在你前后的几位。累计能量是你每一周的总和，不会重置。能量仅用于排名，与购买额度无关。按 UTC 日期计分；个人打卡仍按本地日期统计。', reset: '下次重置：',
  publicNote: '周榜只显示随机昵称和本周能量，不展示姓名、邮箱或学习内容。'
} : {
  title: 'This week\u2019s leaderboard', refresh: 'Refresh', loading: 'Loading standings…', error: 'Standings could not be loaded. Please refresh.', energy: 'energy',
  gap: n => `${n} more energy to move ahead of the next learner.`, encourage: 'Practice at your own pace. Every bit adds up.', you: 'you', lifetime: 'Lifetime energy',
  empty: 'Start practicing this week to join a study group and compete on energy.', emptyGroup: 'You are in this week’s group. One more minute of practice puts you on the board.',
  rules: 'Scoring rules', ruleText: 'Each full minute of listening earns 1 energy, up to 120 per day. Each completed pronunciation assessment earns 1, up to 120 per day. Groups are reshuffled every Monday with up to 15 learners each, and you are ranked only within your group. The board shows the learners just around you. Lifetime energy adds up every week and never resets. Energy is only for ranking, separate from purchased credits. Scoring uses UTC days. Personal check-ins use your local date.', reset: 'Next reset:',
  publicNote: 'The leaderboard only shows a random alias and weekly energy, never your name, email or study content.'
})
const resetLabel = computed(() => new Date(board.value.resetsAt).toLocaleString(locale.value === 'zh' ? 'zh-CN' : 'en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }))
async function load() {
  const uid = user.value?.uid
  if (!uid) return
  const request = ++generation
  busy.value = true
  error.value = false
  try {
    const result = await apiPost('/api/learning/leaderboard', {})
    if (request !== generation || user.value?.uid !== uid) return
    board.value = result
    clearTimeout(resetTimer)
    resetTimer = setTimeout(() => { board.value = null; void load() }, Math.max(1000, new Date(result.resetsAt).getTime() - Date.now()))
  } catch { if (request === generation) error.value = true }
  finally { if (request === generation) busy.value = false }
}
watch(() => user.value?.uid, () => { generation++; clearTimeout(resetTimer); board.value = null; busy.value = false; void load() }, { immediate: true })
onUnmounted(() => { generation++; clearTimeout(resetTimer) })
</script>

<style scoped>
/* 和打卡页其他面板同一种样式 */
.leaderboard {
  padding: var(--space-5);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  background: var(--surface-panel);
  color: var(--ink-primary);
  font-size: var(--font-size-sm);
}

.board-head,
.standings li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
}

h2 {
  margin: 0;
  font-size: var(--font-size-md);
  font-weight: 600;
  line-height: 1.4;
}

p {
  margin: 0;
  color: var(--ink-secondary);
  line-height: 1.6;
}

button {
  font: inherit;
  cursor: pointer;
}

button:disabled {
  opacity: 0.4;
  cursor: default;
}

button:focus-visible,
summary:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring-brand-strong);
}

.text-button {
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--signal-active-strong);
  font-size: var(--font-size-sm);
  font-weight: 600;
}

.board-state {
  padding: var(--space-4) 0;
}

.standing {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: var(--space-1) var(--space-4);
  margin-top: var(--space-3);
  padding-bottom: var(--space-4);
}

.standing strong {
  font-family: var(--font-family-data);
  font-size: var(--font-size-3xl);
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  line-height: 1.1;
}

.standing small {
  margin-left: var(--space-2);
  color: var(--ink-secondary);
  font-family: var(--font-family-base);
  font-size: var(--font-size-xs);
  font-weight: 400;
}

.standing p {
  flex: 1;
  min-width: 12em;
}

/* 累计能量：只给自己看，不参与排名 */
.lifetime {
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
  white-space: nowrap;
}

.lifetime strong {
  color: var(--ink-primary);
  font-family: var(--font-family-data);
  font-weight: 500;
  font-variant-numeric: tabular-nums;
}

.standings {
  margin: 0;
  padding: 0;
  list-style: none;
}

.standings li {
  min-height: 44px;
  padding: 0 var(--space-3);
  border-radius: var(--radius-md);
}

.standings li + li {
  border-top: 1px solid var(--border-subtle);
}

.standings .mine {
  border-top-color: transparent;
  background: var(--surface-active);
}

.standings .mine + li {
  border-top-color: transparent;
}

.rank {
  width: 2em;
  color: var(--ink-tertiary);
  font-family: var(--font-family-data);
  font-variant-numeric: tabular-nums;
}

.alias {
  flex: 1;
  overflow-wrap: anywhere;
}

.alias small {
  color: var(--signal-active-strong);
  font-size: var(--font-size-xs);
}

.standings strong {
  font-family: var(--font-family-data);
  font-weight: 500;
  font-variant-numeric: tabular-nums;
}

.board-rules {
  margin-top: var(--space-4);
  padding-top: var(--space-3);
  border-top: 1px solid var(--border-subtle);
}

.board-rules summary {
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
  cursor: pointer;
}

.board-rules p {
  margin-top: var(--space-2);
  font-size: var(--font-size-xs);
}
</style>
